//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as screenCaptureApi from '@apis/screen-capture/screenCaptureApi';
import {
  SCREEN_CAPTURE_PAGES,
  IScreenCaptureDateRange,
} from '@apis/screen-capture/screenCaptureApiModels';
import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
// import { InstagramInteractiveCalendarView } from '@instagram-account/redux/schedule/instagramScheduleModel';
import { getAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { fetchDefaultInstagramAccount } from '@instagram-account/redux/account/fetchDefaultInstagramAccount';
import {
  DATE_RANGE_INTERVAL_PARAM,
  FILENAME_DATE_FORMAT,
  IInstagramScheduleExportState,
} from '@instagram-account/redux/schedule/export/instagramScheduleExportModel';
import {
  addFeedPreviewExportFailureToast,
  addFeedPreviewExportProgressToast,
} from '@instagram-account/redux/schedule/export/instagramScheduleExportUtils';
import { getLatestFeedPreviewPostDate } from '@instagram-account/redux/schedule/export/selectors/getLatestFeedPreviewPostDate';
import * as instagramScheduleFeedPreviewActions from '@instagram-account/redux/schedule/feed-preview/instagramScheduleFeedPreviewSlice';
import * as instagramScheduleActions from '@instagram-account/redux/schedule/instagramScheduleSlice';
import { getDefaultInstagramAccountUsername } from '@instagram-account/redux/selectors/getDefaultInstagramAccountInfo';
import DateString from '@models/DateString';
import Interval from '@models/Interval';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { getUserFullName } from '@redux/selectors/getUserFullName';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: IInstagramScheduleExportState = {
  errors: {
    fetchScheduleExportError: undefined,
  },
  flags: {
    hasFetchedScheduleExport: false,
    isExportModalOpen: false,
    isFetchingFeedPreviewExport: false,
    isFetchingScheduleExport: false,
  },
  // We set an initial dateRange so our selectors don't explode,
  // but this will be overwritten in the call to open the export modal.
  dateRange: getAliasedDateRange('today', TimeZoneId.Utc),
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramAccountScheduleExport,
  initialState,
  reducers: {
    openExportModal(state) {
      state.flags.isExportModalOpen = true;
    },
    closeExportModal(state) {
      state.flags.isExportModalOpen = false;
      state.flags.hasFetchedScheduleExport = false;
      state.errors.fetchScheduleExportError = undefined;
    },
    setDateRange(state, action: PayloadAction<IAliasedDateRange>) {
      state.dateRange = action.payload;
    },
    requestScheduleExport(state) {
      state.errors.fetchScheduleExportError = undefined;
      state.flags.isFetchingScheduleExport = true;
    },
    receiveScheduleExportSucceeded(state) {
      state.flags.isFetchingScheduleExport = false;
      state.flags.hasFetchedScheduleExport = true;
    },
    receiveScheduleExportFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingScheduleExport = false;
      state.errors.fetchScheduleExportError =
        action.payload || 'Failed to download schedule. Try again later!';
    },
    requestFeedPreviewExport(state) {
      state.errors.fetchFeedPreviewExportError = undefined;
      state.flags.isFetchingFeedPreviewExport = true;
    },
    receiveFeedPreviewExportSucceeded(state) {
      state.flags.isFetchingFeedPreviewExport = false;
    },
    receiveFeedPreviewExportFailed(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.flags.isFetchingFeedPreviewExport = false;
      state.errors.fetchFeedPreviewExportError =
        action.payload || 'Failed to download feed preview. Try again later!';
    },
  },
});

export const { closeExportModal, setDateRange } = actions;

export const openExportModal = createThunk(() => async (dispatch, getState) => {
  const view = getState().instagram.schedule.view;
  const tz = getUserTimeZone(getState());

  const initialDateRange = (() => {
    switch (view) {
      case UniversalCalendarView.Month:
        return getAliasedDateRange('next30Days', tz);
      default:
        return getAliasedDateRange('next7Days', tz);
    }
  })();

  dispatch(setDateRange(initialDateRange));
  dispatch(actions.openExportModal());
});

export const initializeScheduleExport = createThunk(
  (dateRange: IScreenCaptureDateRange) => async (dispatch) => {
    const { startDate, endDate } = dateRange;
    const posts = await dispatch(
      instagramScheduleActions.getAndParseAllScheduledItems(startDate, endDate),
    );

    dispatch(instagramScheduleActions.setScheduledPosts(posts));
  },
);

export const initializeFeedPreviewExport = createThunk(
  () => async (dispatch, getState) => {
    const tz = getUserTimeZone(getState());

    const [posts] = await Promise.all([
      dispatch(
        instagramScheduleActions.getAndParseAllScheduledItems(
          DateString.getToday(tz),
        ),
      ),
      dispatch(instagramScheduleFeedPreviewActions.fetchLivePosts()),
      // Fetch the connected IG account to display header UI for the feed preview
      dispatch(fetchDefaultInstagramAccount()),
    ]);

    dispatch(instagramScheduleActions.setScheduledPosts(posts));
  },
);

export const fetchScheduleExport = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.requestScheduleExport());

    const { startDate, endDate } =
      getState().instagram.schedule.export.dateRange;
    const username = getDefaultInstagramAccountUsername(getState());
    const creatorName = getUserFullName(getState());
    const timeZone = getUserTimeZone(getState());

    if (username) {
      const formattedStart = DateString.format(startDate, FILENAME_DATE_FORMAT);
      const formattedEnd = DateString.format(endDate, FILENAME_DATE_FORMAT);

      /**
       * We can't use the traditional dateRange param for this export because
       * the session date range infrastructure can't handle future date ranges,
       * so we'll send this interval query param instead.
       */
      const dateRangeInterval = Interval.fromDateStrings(startDate, endDate);

      try {
        await screenCaptureApi.downloadPdfReport({
          pageName: SCREEN_CAPTURE_PAGES.INSTAGRAM_SCHEDULE,
          creatorName,
          filename: `schedule_${formattedStart}-${formattedEnd}_${username}`,
          timeZone,
          urlParams: { [DATE_RANGE_INTERVAL_PARAM]: dateRangeInterval },
        });
        dispatch(actions.receiveScheduleExportSucceeded());
      } catch (e: unknown) {
        dispatch(
          actions.receiveScheduleExportFailed(getRequestErrorMessage(e)),
        );
      }
    } else {
      dispatch(actions.receiveScheduleExportFailed());
    }
  },
);

export const fetchFeedPreviewExport = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.requestFeedPreviewExport());
    dispatch(addFeedPreviewExportProgressToast());

    const latestPostDate = getLatestFeedPreviewPostDate(getState());

    const username = getDefaultInstagramAccountUsername(getState());
    const creatorName = getUserFullName(getState());

    if (username) {
      const formattedDate = DateString.format(
        latestPostDate,
        FILENAME_DATE_FORMAT,
      );

      try {
        await screenCaptureApi.downloadPdfReport({
          pageName: SCREEN_CAPTURE_PAGES.INSTAGRAM_FEED_PREVIEW,
          creatorName,
          filename: `feedpreview_${formattedDate}_${username}`,
        });
        dispatch(actions.receiveFeedPreviewExportSucceeded());
      } catch (e: unknown) {
        dispatch(
          actions.receiveFeedPreviewExportFailed(getRequestErrorMessage(e)),
        );
        dispatch(addFeedPreviewExportFailureToast());
      }
    } else {
      dispatch(actions.receiveFeedPreviewExportFailed());
    }
  },
);

export default reducer;
